/**
 * Vista 13 · Calendario de días no hábiles.
 *
 * `suspende_plazo` distingue dos cosas que parecen iguales y no lo son: un
 * feriado que **suspende** el cómputo del plazo, y un día no laborable
 * compensable que no lo suspende. Confundirlas mueve vencimientos, y un
 * vencimiento movido es un oficio que se reitera antes de tiempo.
 *
 * El recálculo no es un proceso: los plazos se derivan al leer (`DAT-7`), así
 * que registrar un feriado ya cambia lo que se ve, sin migración.
 */
import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Switch } from '@mpfn/ui'
import {
  Callout,
  DataTable,
  DatePicker,
  FormField,
  type DataTableColumn,
} from '@mpfn/patterns'
import { Trash2 } from 'lucide-react'
import { useCalendario, useGuardarCalendario } from '../api/consultas.js'
import type { DiaNoHabil } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { Interruptor } from '../componentes/Conmutadores.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { fecha, hoy } from '../lib/formato.js'

const MAXIMO_DIAS = 400

export function Calendario() {
  const calendario = useCalendario()

  return (
    <Pagina
      titulo="Calendario de días no hábiles"
      descripcion="Los plazos se cuentan en días hábiles: lo que se registre aquí los desplaza."
    >
      <Datos estado={calendario}>{(dias) => <Editor dias={dias} />}</Datos>
    </Pagina>
  )
}

function Editor({ dias }: { dias: readonly DiaNoHabil[] }) {
  const guardar = useGuardarCalendario()
  const [borrador, setBorrador] = useState<readonly DiaNoHabil[]>(dias)
  const [nuevaFecha, setNuevaFecha] = useState(hoy())
  const [descripcion, setDescripcion] = useState('')
  const [suspende, setSuspende] = useState(true)

  const duplicada = borrador.some((dia) => dia.fecha === nuevaFecha)
  const lleno = borrador.length >= MAXIMO_DIAS

  const columnas: ReadonlyArray<DataTableColumn<DiaNoHabil>> = [
    { id: 'fecha', header: 'Fecha', cell: (fila) => fecha(fila.fecha) },
    { id: 'descripcion', header: 'Descripción', cell: (fila) => fila.descripcion },
    {
      id: 'suspende',
      header: 'Suspende el plazo',
      cell: (fila) => (
        <Switch
          checked={fila.suspendePlazo}
          aria-label={`Suspende el plazo el ${fecha(fila.fecha)}`}
          onCheckedChange={(valor) => {
            setBorrador((previo) =>
              previo.map((dia) => (dia.fecha === fila.fecha ? { ...dia, suspendePlazo: valor } : dia)),
            )
          }}
        />
      ),
    },
    {
      id: 'quitar',
      header: '',
      align: 'right',
      cell: (fila) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Quitar el ${fecha(fila.fecha)}`}
          onClick={() => {
            setBorrador((previo) => previo.filter((dia) => dia.fecha !== fila.fecha))
          }}
        >
          <Trash2 size={14} aria-hidden="true" />
        </Button>
      ),
    },
  ]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Agregar un día</CardTitle>
        </CardHeader>
        <CardContent className="sigfisc-formulario">
          <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
            <FormField
              id="dia-fecha"
              label="Fecha"
              required
              {...(duplicada ? { error: 'Esa fecha ya está registrada.' } : {})}
            >
              <DatePicker
                id="dia-fecha"
                value={nuevaFecha}
                onChange={setNuevaFecha}
                yearRange={{ from: Number(hoy().slice(0, 4)), to: Number(hoy().slice(0, 4)) + 2 }}
              />
            </FormField>

            <FormField id="dia-descripcion" label="Descripción" required>
              <Input
                value={descripcion}
                placeholder="Fiestas Patrias"
                onChange={(evento) => {
                  setDescripcion(evento.target.value)
                }}
              />
            </FormField>
          </div>

          <Interruptor
            etiqueta="Suspende el cómputo del plazo"
            marcado={suspende}
            onCambio={setSuspende}
            descripcion="Desmárquelo para un día no laborable que no suspende plazos."
          />

          <Button
            variant="secondary"
            disabled={duplicada || descripcion.trim() === '' || lleno}
            onClick={() => {
              setBorrador((previo) =>
                [...previo, { fecha: nuevaFecha, descripcion: descripcion.trim(), suspendePlazo: suspende }].sort(
                  (uno, otro) => uno.fecha.localeCompare(otro.fecha),
                ),
              )
              setDescripcion('')
            }}
          >
            Agregar al borrador
          </Button>

          {lleno && (
            <Callout tone="warning" title="Máximo alcanzado">
              No se pueden guardar más de {MAXIMO_DIAS} días por carga.
            </Callout>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Días registrados · {borrador.length}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columnas}
            rows={borrador}
            rowKey={(fila) => fila.fecha}
            empty={{
              title: 'Sin días registrados',
              description: 'Los plazos se cuentan salteando sábados y domingos aunque esta lista esté vacía.',
            }}
          />
        </CardContent>
      </Card>

      <Button
        variant="primary"
        loading={guardar.isPending}
        onClick={() => {
          guardar.mutate(borrador, {
            onSuccess: (datos) => {
              avisarExito(
                `Se registraron ${plural(datos.dias.length, 'día', 'días')}.`,
                datos.consultasConPlazoEnCurso > 0
                  ? `Hay ${plural(datos.consultasConPlazoEnCurso, 'consulta', 'consultas')} con plazo en curso; su vencimiento se recalcula al leerlo.`
                  : undefined,
              )
            },
            onError: (error) => {
              avisarError(error, 'No se pudo guardar el calendario')
            },
          })
        }}
      >
        Guardar el calendario
      </Button>
    </>
  )
}
